import { LayoutDashboard, TrendingUp, History, PieChart, RefreshCw, ChevronRight, ChevronLeft, Search, ArrowUpRight, ArrowDownRight, Home, BarChart3, CandlestickChart, LineChart, ChevronUp, ChevronDown, Sun, Moon, Target, Info, Ruler, Table, FileSpreadsheet, ExternalLink, Settings, Check, Edit2, Upload, FileText, X, AlertCircle, Activity, Coins, Tag, Eye, Calendar as CalendarIcon, Wallet, Filter, Plus, Trash2, Loader2, LogOut, ShieldCheck, ShoppingBag, Briefcase, Database, Users, HelpCircle, FolderOpen, Save, Layers } from 'lucide-react';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { fetchTransactions, saveTransactionToDB, deleteTransactionFromDB, parseUploadedCsv, updateUserActivity, fetchUserPortfolios, saveBatchTransactionsToDB, fetchUsers } from './services/dataService';
import { fetchBinanceKlines, fetchCurrentPrices, isSymbolSupported, fetchCoinMetadata, clearKlinesCache, getCachedKlines, PriceData } from './services/marketService';
import { Transaction, Candle, PortfolioStats, User } from './types';
import TradingChart from './components/TradingChart';
import SummaryModal from './components/SummaryModal';
import HoldingsModal from './components/HoldingsModal';
import HistogramModal from './components/HistogramModal';
import LatestBuysModal from './components/LatestBuysModal';
import TransactionsModal from './components/TransactionsModal';
import PortfolioHistoryChart from './components/PortfolioHistoryChart';
import PortfolioCalendar from './components/PortfolioCalendar';
import AddTransactionModal from './components/AddTransactionModal';
import ConfirmModal from './components/ConfirmModal';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import CsvHelpModal from './components/CsvHelpModal';
import DbHelpModal from './components/DbHelpModal';
import CryptoTicker from './components/CryptoTicker';
import CsvUploadOtpModal from './components/CsvUploadOtpModal';
import FearGreedWidget from './components/FearGreedWidget';
import * as XLSX from 'xlsx';

type SortKey = 'displayTotal' | 'roi' | 'ventasPerc' | 'totalProfit' | 'currentPrice' | 'ticket' | 'rank';
type SortOrder = 'asc' | 'desc' | 'none';
type Theme = 'dark' | 'light';
type PriceDirection = 'up' | 'down' | 'stable';
type MainTab = 'trading' | 'history' | 'calendar' | 'transactions';
type AssetType = 'crypto' | 'stocks';

interface SortConfig {
  key: SortKey;
  order: SortOrder;
}

interface HistoryStats {
  value: number;
  cost: number;
  pnl: number;
  pnlPerc: number;
  changeMonth: number;
  changeMonthPerc: number;
  changeWeek?: number;
  changeWeekPerc?: number;
  changeYear?: number;
  changeYearPerc?: number;
  change24h?: number;
  change24hPerc?: number;
}

// Ranking de mercado para ordenar la lista de activos
const ASSET_RANKING: Record<string, number> = {
  // CRIPTOMONEDAS
  'BTC': 1, 'ETH': 2, 'BNB': 3, 'SOL': 4, 'XRP': 5, 'USDT': 6, 'ADA': 7, 'DOGE': 8, 'TRX': 9, 'TON': 10,
  'DOT': 11, 'LINK': 12, 'AVAX': 13, 'SHIB': 14, 'WBTC': 15, 'BCH': 16, 'NEAR': 17, 'MATIC': 18, 'LEO': 19, 'UNI': 20,
  'LTC': 21, 'ICP': 22, 'PEPE': 23, 'DAI': 24, 'KAS': 25, 'ETC': 26, 'APT': 27, 'FIL': 28, 'STX': 29, 'XLM': 30,
  // Fix: Removed duplicate key 'NEAR' (previously 17 and 31). Replaced 31 with 'LDO'.
  'LDO': 31, 'TAO': 32, 'RENDER': 33, 'VET': 34, 'HBAR': 35, 'OKB': 36, 'ATOM': 37, 'OP': 38, 'IMX': 39, 'WIF': 40,
  'ARB': 41, 'TIA': 42, 'KAVA': 43, 'MKR': 44, 'FTM': 45, 'GRT': 46, 'FLOW': 47, 'EOS': 48, 'ALGO': 49, 'AAVE': 50,
  // ACCIONES (TOP POR CAPITALIZACIÓN)
  'NVDA': 1, 'AAPL': 2, 'MSFT': 3, 'GOOGL': 4, 'AMZN': 5, 'META': 6, 'TSLA': 7, 'BRK.B': 8, 'LLY': 9, 'AVGO': 10,
  'V': 11, 'JPM': 12, 'NVO': 13, 'MA': 14, 'WMT': 15, 'XOM': 16, 'PG': 17, 'COST': 18, 'ORCL': 19, 'HD': 20
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => localStorage.getItem('cp_auth') === 'true');
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('cp_user');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [assetType, setAssetType] = useState<AssetType>(() => (localStorage.getItem('cp_asset_type') as AssetType) || 'crypto');
  const [dataSource, setDataSource] = useState<'cloud' | 'csv'>('cloud');
  const [csvData, setCsvData] = useState<Transaction[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statsScrollRef = useRef<HTMLDivElement>(null);
  
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [currentPrices, setCurrentPrices] = useState<Record<string, PriceData>>({});
  const [coinIcons, setCoinIcons] = useState<Record<string, string>>({});
  const [priceDirections, setPriceDirections] = useState<Record<string, PriceDirection>>({});
  const prevPricesRef = useRef<Record<string, number>>({});
  
  const [activeTab, setActiveTab] = useState<MainTab>('trading');
  const [selectedSymbol, setSelectedSymbol] = useState<string>('BTC-USD');
  const [selectedInterval, setSelectedInterval] = useState<string>('1d');
  const [chartType, setChartType] = useState<'candlestick' | 'line'>('candlestick');
  const [candles, setCandles] = useState<Candle[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [chartFetching, setChartFetching] = useState<boolean>(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  
  const [refreshCounter, setRefreshCounter] = useState<number>(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'rank', order: 'asc' });
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('cp_theme') as Theme) || 'light');
  
  const [showBreakEven, setShowBreakEven] = useState<boolean>(() => localStorage.getItem('cp_show_breakeven') === 'true');
  const [isMeasureMode, setIsMeasureMode] = useState<boolean>(false);
  const [showChartLabels, setShowChartLabels] = useState<boolean>(() => localStorage.getItem('cp_show_labels') === 'true');
  const [showEMA, setShowEMA] = useState<boolean>(() => localStorage.getItem('cp_show_ema') === 'true');
  const [emaPeriod, setEmaPeriod] = useState<number>(55);
  const [useDynamicOpacity, setUseDynamicOpacity] = useState<boolean>(() => localStorage.getItem('cp_dyn_opacity') !== 'false');
  const [useUniformBubbleSize, setUseUniformBubbleSize] = useState<boolean>(() => localStorage.getItem('cp_uniform_bubbles') === 'true');
  
  const [transactionsSearchTerm, setTransactionsSearchTerm] = useState<string>('');
  const [transactionsTypeFilter, setTransactionsTypeFilter] = useState<'all' | 'buy' | 'sell'>('all');
  const [assetsSearchTerm, setAssetsSearchTerm] = useState<string>('');
  
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState<boolean>(false);
  const [isHoldingsModalOpen, setIsHoldingsModalOpen] = useState<boolean>(false);
  const [isHistogramModalOpen, setIsHistogramModalOpen] = useState<boolean>(false);
  const [isLatestBuysModalOpen, setIsLatestBuysModalOpen] = useState<boolean>(false);
  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState<boolean>(false);
  const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] = useState<boolean>(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false);
  const [isCsvHelpModalOpen, setIsCsvHelpModalOpen] = useState<boolean>(false);
  const [isDbHelpModalOpen, setIsDbHelpModalOpen] = useState<boolean>(false);
  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState<boolean>(false);
  
  const [transactionToDelete, setTransactionToDelete] = useState<string | null>(null);
  const [transactionToEdit, setTransactionToEdit] = useState<Transaction | null>(null);
  
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [hideSmallBalances, setHideSmallBalances] = useState<boolean>(true);
  
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isChartMaximized, setIsChartMaximized] = useState<boolean>(false);
  const [isChartExpandedDown, setIsChartExpandedDown] = useState<boolean>(true);
  const [isCalendarMaximized, setIsCalendarMaximized] = useState<boolean>(false);
  const [isHistoryMaximized, setIsHistoryMaximized] = useState<boolean>(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);

  // Portfolio State
  const [activePortfolio, setActivePortfolio] = useState<string>(() => localStorage.getItem('cp_active_portfolio') || 'Portafolio 1');
  const [portfolioList, setPortfolioList] = useState<string[]>(() => {
     const saved = localStorage.getItem('cp_portfolios');
     return saved ? JSON.parse(saved) : ['Portafolio 1'];
  });
  const [isPortfolioMenuOpen, setIsPortfolioMenuOpen] = useState<boolean>(false);
  
  // NEW MENUS STATES
  const [isDataMenuOpen, setIsDataMenuOpen] = useState<boolean>(false);
  const [isAssetMenuOpen, setIsAssetMenuOpen] = useState<boolean>(false);

  const [historyStats, setHistoryStats] = useState<HistoryStats>({ 
    value: 0, cost: 0, pnl: 0, pnlPerc: 0, changeMonth: 0, changeMonthPerc: 0, changeWeek: 0, changeWeekPerc: 0, changeYear: 0, changeYearPerc: 0, change24h: 0, change24hPerc: 0
  });

  const ICON_LOGO_ID = import.meta.env.VITE_LOGO_ICON_ID || '';

  useEffect(() => {
    localStorage.setItem('cp_show_breakeven', String(showBreakEven));
    localStorage.setItem('cp_show_labels', String(showChartLabels));
    localStorage.setItem('cp_show_ema', String(showEMA));
    localStorage.setItem('cp_dyn_opacity', String(useDynamicOpacity));
    localStorage.setItem('cp_uniform_bubbles', String(useUniformBubbleSize));
  }, [showBreakEven, showChartLabels, showEMA, useDynamicOpacity, useUniformBubbleSize]);

  useEffect(() => {
    localStorage.setItem('cp_active_portfolio', activePortfolio);
  }, [activePortfolio]);

  useEffect(() => {
    localStorage.setItem('cp_portfolios', JSON.stringify(portfolioList));
  }, [portfolioList]);

  const isAnyContentMaximized = 
    (isChartMaximized && activeTab === 'trading') || 
    (isCalendarMaximized && activeTab === 'calendar') ||
    (isHistoryMaximized && activeTab === 'history');

  useEffect(() => {
    document.body.className = theme;
    localStorage.setItem('cp_theme', theme);
  }, [theme]);

  const checkScroll = useCallback(() => {
    if (statsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = statsScrollRef.current;
      setCanScrollLeft(scrollLeft > 1);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
    }
  }, []);

  const scrollStats = (direction: 'left' | 'right') => {
    if (statsScrollRef.current) {
      const scrollAmount = 300;
      statsScrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }
  }, [activeTab, transactions, historyStats, selectedSymbol, theme, isAuthenticated, checkScroll]);

  // REFRESH USER DATA ON MOUNT to sync limits and roles
  useEffect(() => {
    if (isAuthenticated && currentUser) {
        fetchUsers().then(users => {
            const freshUser = users.find(u => u.id === currentUser.id);
            if (freshUser) {
                setCurrentUser(freshUser);
                localStorage.setItem('cp_user', JSON.stringify(freshUser));
            }
        }).catch(err => console.error("Failed to sync user data", err));
    }
  }, [isAuthenticated]);

  const handleUserUpdate = (updatedUser: User) => {
    if (currentUser && currentUser.id === updatedUser.id) {
        setCurrentUser(updatedUser);
        localStorage.setItem('cp_user', JSON.stringify(updatedUser));
    }
  };

  const loadDataFromSupabase = useCallback(async (isSilent = false) => {
    if (!currentUser) return;
    if (!isSilent) setLoading(true);
    try {
      const dbPortfolios = await fetchUserPortfolios(currentUser.username);
      
      const combinedPortfolios = new Set(dbPortfolios);
      if (activePortfolio) {
          combinedPortfolios.add(activePortfolio);
      }
      
      if (combinedPortfolios.size === 0) combinedPortfolios.add('Portafolio 1');

      const validPortfolios = Array.from(combinedPortfolios);
      validPortfolios.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      setPortfolioList(validPortfolios);

      const portfolioToLoad = activePortfolio;
      const filteredData = await fetchTransactions(currentUser.username, portfolioToLoad);
      
      setTransactions(filteredData);
      
      setHistoryStats({ 
        value: 0, cost: 0, pnl: 0, pnlPerc: 0, 
        changeMonth: 0, changeMonthPerc: 0, 
        changeWeek: 0, changeWeekPerc: 0, 
        changeYear: 0, changeYearPerc: 0, 
        change24h: 0, change24hPerc: 0 
      });
      
      const stocksInPortfolio = filteredData
        .filter(t => (t.category || '').toLowerCase() === 'acciones')
        .map(t => t.ticket);
      
      const prices = await fetchCurrentPrices(assetType, Array.from(new Set(stocksInPortfolio)));
      setCurrentPrices(prev => ({ ...prev, ...prices }));
      
      const priceOnly: Record<string, number> = {};
      Object.keys(prices).forEach(k => priceOnly[k] = prices[k].price);
      prevPricesRef.current = { ...prevPricesRef.current, ...priceOnly };
      
      const displayed = filteredData.filter(t => {
          const category = (t.category || 'criptomoneda').toLowerCase();
          const typeMatch = assetType === 'crypto' ? (category === 'criptomoneda' || !category) : category === 'acciones';
          return typeMatch;
      });

      if (displayed.length > 0) {
        updateIcons(filteredData);
        setSelectedSymbol(prev => {
           const stillValid = displayed.some(t => t.ticket === prev);
           if (!stillValid && displayed[0]) return displayed[0].ticket;
           return prev;
        });
      } else {
          setSelectedSymbol(assetType === 'stocks' ? 'AAPL-USD' : 'BTC-USD');
      }

      updateUserActivity(currentUser.id).catch(e => console.error("Activity update failed", e));
      
    } catch (err) {
      console.error("Error loading data from Supabase:", err);
      setTransactions([]);
    }
    if (!isSilent) setLoading(false);
  }, [currentUser, assetType, activePortfolio]);

  useEffect(() => {
    if (isAuthenticated && dataSource === 'cloud' && currentUser) {
      loadDataFromSupabase(transactions.length > 0);
    } else if (isAuthenticated && dataSource === 'csv') {
      if (csvData.length > 0) {
        const filteredCsv = csvData.filter(t => (t.portfolio || 'Portafolio 1') === activePortfolio);
        setTransactions(filteredCsv);
        updateIcons(filteredCsv);
        setLoading(false);
      } else {
        setTransactions([]);
        setLoading(false);
      }
    }
  }, [dataSource, isAuthenticated, currentUser, loadDataFromSupabase, refreshCounter, activePortfolio]);

  useEffect(() => {
    if (isAuthenticated) {
        const interval = setInterval(updatePrices, 10000);
        return () => clearInterval(interval);
    }
  }, [isAuthenticated, assetType, transactions]);

  const activeTransactions = useMemo(() => {
    return transactions.filter(t => {
      const category = (t.category || 'criptomoneda').toLowerCase();
      const typeMatch = assetType === 'crypto' ? (category === 'criptomoneda' || category === '') : category === 'acciones';
      return typeMatch;
    });
  }, [transactions, assetType]);

  const visibleSymbols = useMemo(() => {
      const symbols = new Set(activeTransactions.map(t => t.ticket));
      return Array.from(symbols);
  }, [activeTransactions]);

  useEffect(() => {
    if (!selectedSymbol || !isAuthenticated) return;
    
    const cached = getCachedKlines(selectedSymbol, selectedInterval, assetType);
    if (cached) {
      setCandles(cached);
      setChartFetching(false);
    } else {
      setChartFetching(true);
    }

    const controller = new AbortController();
    const fetchChartData = async () => {
      if (!isSymbolSupported(selectedSymbol, assetType)) return;
      try {
        const klines = await fetchBinanceKlines(selectedSymbol, selectedInterval, controller.signal, assetType);
        if (!controller.signal.aborted) { 
          setCandles(klines); 
          setChartFetching(false);
        }
      } catch (error) { 
        if (!controller.signal.aborted) { 
          console.error("Error fetching klines", error);
          setChartFetching(false);
        } 
      }
    };
    fetchChartData();
    return () => controller.abort();
  }, [selectedSymbol, selectedInterval, isAuthenticated, assetType, refreshCounter]);

  const updateIcons = async (txs: Transaction[]) => {
    const uniqueSymbols = Array.from(new Set(txs.map(t => t.ticket)));
    const typeMap: Record<string, string> = {};
    txs.forEach(t => {
      const base = t.ticket.split(/[-/]/)[0].toUpperCase();
      typeMap[base] = t.category || 'criptomoneda';
    });
    const icons = await fetchCoinMetadata(uniqueSymbols, typeMap);
    setCoinIcons(prev => ({ ...prev, ...icons }));
  };

  const updatePrices = async () => {
    const stockSymbols = assetType === 'stocks' ? visibleSymbols : [];
    const newPricesData = await fetchCurrentPrices(assetType, stockSymbols);
    const newDirections: Record<string, PriceDirection> = {};
    
    Object.keys(newPricesData).forEach(sym => {
      const prevPrice = prevPricesRef.current[sym];
      const newPrice = newPricesData[sym].price;
      if (prevPrice !== undefined) {
        if (newPrice > prevPrice) newDirections[sym] = 'up';
        else if (newPrice < prevPrice) newDirections[sym] = 'down';
        else newDirections[sym] = 'stable';
      } else { newDirections[sym] = 'stable'; }
    });
    
    setPriceDirections(newDirections);
    setCurrentPrices(prev => ({ ...prev, ...newPricesData }));
    
    const priceOnly: Record<string, number> = {};
    Object.keys(newPricesData).forEach(k => priceOnly[k] = newPricesData[k].price);
    prevPricesRef.current = { ...prevPricesRef.current, ...priceOnly };
  };

  const handleLoginSuccess = (user: User) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    localStorage.setItem('cp_auth', 'true');
    localStorage.setItem('cp_user', JSON.stringify(user));
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setTransactions([]);
    setIsUserMenuOpen(false);
    localStorage.removeItem('cp_auth');
    localStorage.removeItem('cp_user');
    localStorage.removeItem('cp_asset_type');
    localStorage.removeItem('cp_active_portfolio');
    localStorage.removeItem('cp_portfolios'); 
  };

  const switchToAssetType = (type: AssetType) => {
    if (assetType === type) return;
    
    setAssetType(type);
    localStorage.setItem('cp_asset_type', type);
    setRefreshing(true);
    
    const newDisplayed = transactions.filter(t => {
        const category = (t.category || 'criptomoneda').toLowerCase();
        const typeMatch = type === 'crypto' ? (category === 'criptomoneda' || !category) : category === 'acciones';
        return typeMatch;
    });
    
    if (newDisplayed.length > 0) {
        const unique = Array.from(new Set(newDisplayed.map(t => t.ticket)));
        setSelectedSymbol(unique[0]);
    } else {
        setSelectedSymbol(type === 'stocks' ? 'AAPL-USD' : 'BTC-USD');
    }
    
    updatePrices().finally(() => {
        setTimeout(() => setRefreshing(false), 800);
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    clearKlinesCache();
    if (dataSource === 'cloud') { await loadDataFromSupabase(true); } 
    else { await updatePrices(); await updateIcons(transactions); }
    setRefreshCounter(prev => prev + 1);
    setRefreshing(false);
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, order: 'desc' };
      if (prev.order === 'desc') return { key, order: 'asc' };
      if (prev.order === 'asc') return { key, order: 'none' };
      return { key, order: 'desc' };
    });
  };

  const toggleTheme = () => { setTheme(prev => prev === 'dark' ? 'light' : 'dark'); };

  const switchToCloud = () => setDataSource('cloud');
  const switchToCsv = () => {
    if (csvData.length === 0) fileInputRef.current?.click();
    else setDataSource('csv');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        const parsedData = parseUploadedCsv(text, activePortfolio); 
        if (parsedData.length > 0) {
            setCsvData(parsedData);
            const filtered = parsedData.filter(t => (t.portfolio || 'Portafolio 1') === activePortfolio);
            setTransactions(filtered);
            setDataSource('csv');
            await updateIcons(parsedData);
            const uniqueSymbols = Array.from(new Set(parsedData.map(t => t.ticket)));
            const probableAssetType = parsedData[0].category === 'acciones' ? 'stocks' : 'crypto';
            setAssetType(probableAssetType);
            const prices = await fetchCurrentPrices(probableAssetType, uniqueSymbols);
            setCurrentPrices(prev => ({ ...prev, ...prices }));
            if (parsedData.length > 0) { setSelectedSymbol(parsedData[0].ticket); }
        }
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };
  
  const handleAddTransaction = (tx: Transaction) => {
    const transactionWithUser = { ...tx };
    if (!transactionWithUser.portfolioUser && currentUser) { transactionWithUser.portfolioUser = currentUser.username; }
    
    if (!transactionWithUser.portfolio) {
        transactionWithUser.portfolio = activePortfolio;
    }

    setTransactions(prev => {
      const existingIndex = prev.findIndex(t => t.id === transactionWithUser.id);
      if (existingIndex >= 0) { const next = [...prev]; next[existingIndex] = transactionWithUser; return next; }
      return [transactionWithUser, ...prev];
    });
    if (dataSource === 'csv') {
      setCsvData(prev => {
        const existingIndex = prev.findIndex(t => t.id === transactionWithUser.id);
        if (existingIndex >= 0) { const next = [...prev]; next[existingIndex] = transactionWithUser; return next; }
        return [transactionWithUser, ...prev];
      });
    }
    setTimeout(() => {
      updateIcons([transactionWithUser]);
      if (dataSource === 'cloud') {
        setSyncing(true);
        saveTransactionToDB(transactionWithUser).finally(() => setSyncing(false));
      }
    }, 10);
  };

  const confirmDelete = (id: string) => { setTransactionToDelete(id); setIsConfirmDeleteOpen(true); };

  const handleDeleteTransaction = () => {
    if (!transactionToDelete) return;
    const id = transactionToDelete;
    setTransactions(prev => prev.filter(t => t.id !== id));
    if (dataSource === 'csv') setCsvData(prev => prev.filter(t => t.id !== id));
    setTransactionToDelete(null);
    if (dataSource === 'cloud') {
      setSyncing(true);
      deleteTransactionFromDB(id).finally(() => setSyncing(false));
    }
  };

  const handleAddPortfolio = async () => {
    const userLimit = currentUser?.maxPortfolios || 1;
    const nums = portfolioList.map(p => parseInt(p.split(' ')[1]) || 0);
    const maxNum = Math.max(...nums, 0);
    const nextNum = maxNum + 1;

    if (nextNum > userLimit) return; 

    const newName = `Portafolio ${nextNum}`;
    setPortfolioList(prev => {
        if (prev.includes(newName)) return prev;
        return [...prev, newName].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    });
    setActivePortfolio(newName);
    setIsPortfolioMenuOpen(false);
  };

  const handleCsvSyncSuccess = async () => {
    if (!currentUser) return;
    const transactionsToSave = csvData.map(t => ({
        ...t,
        portfolioUser: currentUser.username,
        portfolio: activePortfolio 
    }));

    await saveBatchTransactionsToDB(transactionsToSave);
    setDataSource('cloud');
    await loadDataFromSupabase();
  };

  const handleDownloadExcel = () => {
    const dataToExport = filteredFullTransactions.map(t => ({
      ID: t.id,
      Tipo: t.status,
      Ticket: t.ticket,
      Simbolo: t.symbolName,
      Precio: t.price,
      Cantidad: t.quantity,
      Total: t.total,
      Fecha: t.date,
      Hora: t.time,
      Categoria: t.category || 'criptomoneda',
      Portafolio: t.portfolio || 'Portafolio 1'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    
    const wscols = [
      { wch: 20 },
      { wch: 10 },
      { wch: 12 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 }
    ];
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transacciones");
    XLSX.writeFile(wb, `Transacciones_${activePortfolio.replace(' ', '_')}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const symbolMetrics = useMemo(() => {
    const metrics: Record<string, any> = {};
    activeTransactions.forEach(t => {
      if (!metrics[t.ticket]) {
        metrics[t.ticket] = { ticket: t.ticket, symbolName: t.symbolName, totalBuy: 0, totalSell: 0, qtyBuy: 0, qtySell: 0, firstOccurrence: activeTransactions.findIndex(tr => tr.ticket === t.ticket) };
      }
      const isBuy = t.status?.toLowerCase().includes('compra') || t.status?.toLowerCase().includes('buy');
      const isSell = t.status?.toLowerCase().includes('venta') || t.status?.toLowerCase().includes('sell');
      if (isBuy) { metrics[t.ticket].totalBuy += t.total; metrics[t.ticket].qtyBuy += t.quantity; } 
      else if (isSell) { metrics[t.ticket].totalSell += t.total; metrics[t.ticket].qtySell += t.quantity; }
    });
    const results = Object.values(metrics).map((m: any) => {
      const binanceKey = m.ticket.replace(/[-/]/g, '').replace('USD', 'USDT');
      const priceData = currentPrices[m.ticket] || currentPrices[binanceKey];
      const currentPrice = priceData?.price || 0;
      const change24h = priceData?.change24h || 0;
      const currentQty = Math.max(0, m.qtyBuy - m.qtySell);
      const averageBuyPrice = m.qtyBuy > 0 ? m.totalBuy / m.qtyBuy : 0;
      const currentVal = currentQty * currentPrice;
      const totalProfit = (currentVal + m.totalSell) - m.totalBuy;
      const roi = m.totalBuy > 0 ? (totalProfit / m.totalBuy) * 100 : 0;
      const ventasPerc = m.totalBuy > 0 ? (m.totalSell / m.totalBuy) * 100 : 0;
      const pricePerf = averageBuyPrice > 0 ? ((currentPrice - averageBuyPrice) / averageBuyPrice) * 100 : 0;
      
      // Ranking de mercado: si no está en la lista, se pone al final (9999)
      const rank = ASSET_RANKING[m.ticket.split(/[-/]/)[0].toUpperCase()] || 9999;
      
      return { ...m, displayTotal: m.totalBuy, roi, ventasPerc, averageBuyPrice, breakEven: averageBuyPrice, currentQty, currentVal, totalProfit, currentPrice, pricePerf, rank, change24h };
    });

    if (sortConfig.order === 'none') { 
        return results.sort((a: any, b: any) => a.firstOccurrence - b.firstOccurrence); 
    }

    return results.sort((a: any, b: any) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];
      
      if (typeof valA === 'string') {
          return sortConfig.order === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
      }
      
      // Corrección de ordenación numérica robusta
      return sortConfig.order === 'desc' ? valB - valA : valA - valB;
    });
  }, [activeTransactions, currentPrices, sortConfig]);

  const { liveTotalValue, livePnl, livePnlPerc, liveChange24h, liveChange24hPerc, totalInvested, totalSold } = useMemo(() => {
    const liveTotalValue = symbolMetrics.reduce((acc, m) => acc + (m.currentVal || 0), 0);
    const totalInvested = symbolMetrics.reduce((acc, m) => acc + (m.totalBuy || 0), 0);
    const totalSold = symbolMetrics.reduce((acc, m) => acc + (m.totalSell || 0), 0);
    const livePnl = (liveTotalValue + totalSold) - totalInvested;
    const livePnlPerc = totalInvested > 0 ? (livePnl / totalInvested) * 100 : 0;
    const valDiff = liveTotalValue - (historyStats.value || 0);
    const liveChange24h = (historyStats.change24h || 0) + valDiff;
    const yesterdayValue = liveTotalValue - liveChange24h;
    const liveChange24hPerc = yesterdayValue > 0 ? (liveChange24h / yesterdayValue) * 100 : 0;
    return { liveTotalValue, livePnl, livePnlPerc, liveChange24h, liveChange24hPerc, totalInvested, totalSold };
  }, [symbolMetrics, historyStats]);

  const visibleAssets = useMemo(() => {
    let filtered = symbolMetrics;
    
    const wouldBeEmpty = filtered.filter(m => (m.currentVal >= 0.1)).length === 0;
    const lockFilter = refreshing || (wouldBeEmpty && symbolMetrics.length > 0);

    if (hideSmallBalances && !lockFilter) {
      filtered = filtered.filter(m => {
        const isClosed = m.qtyBuy <= m.qtySell;
        const isDust = m.currentQty < (m.qtyBuy * 0.005);
        const hasNoValue = m.currentVal < 0.1;
        return !isClosed && !isDust && !hasNoValue;
      });
    }
    
    if (assetsSearchTerm.trim()) {
      const search = assetsSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(m => m.ticket.toLowerCase().includes(search) || m.symbolName.toLowerCase().includes(search));
    }
    return filtered;
  }, [symbolMetrics, hideSmallBalances, assetsSearchTerm, refreshing]);

  const displayedAssetTickets = useMemo(() => visibleAssets.map(a => a.ticket), [visibleAssets]);

  const topPerformer = useMemo(() => {
    if (!visibleAssets || visibleAssets.length === 0) return null;
    return [...visibleAssets].sort((a, b) => b.roi - a.roi)[0];
  }, [visibleAssets]);

  const filteredFullTransactions = useMemo(() => {
    let filtered = activeTransactions;

    if (transactionsTypeFilter !== 'all') {
      filtered = filtered.filter(t => {
        const isBuy = t.status?.toLowerCase().includes('compra') || t.status?.toLowerCase().includes('buy');
        return transactionsTypeFilter === 'buy' ? isBuy : !isBuy;
      });
    }

    if (!transactionsSearchTerm.trim()) return filtered;
    const search = transactionsSearchTerm.toLowerCase().trim();
    return filtered.filter(t => t.ticket.toLowerCase().includes(search) || t.symbolName.toLowerCase().includes(search));
  }, [activeTransactions, transactionsSearchTerm, transactionsTypeFilter]);

  const formatLivePrice = (price: number) => {
    if (price === 0 || isNaN(price)) return '0.00';
    let decimals = price < 1 ? 4 : (price < 10 ? 3 : 2);
    return price.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const getPriceColorClass = (symbol: string) => {
    if (refreshing || (currentPrices[symbol]?.price === 0)) return 'text-slate-500 opacity-40';
    const binanceKey = symbol.replace(/[-/]/g, '').replace('USD', 'USDT');
    const direction = priceDirections[symbol] || priceDirections[binanceKey];
    return direction === 'down' ? 'text-[#f23645]' : 'text-[#089981]';
  };

  const getIconUrl = (symbol: string) => {
    const base = symbol.split(/[-/]/)[0].toUpperCase();
    return coinIcons[base] || '';
  };

  const themeClasses = useMemo(() => ({
    bg: 'theme-bg-main',
    panel: 'theme-bg-panel',
    border: 'theme-border',
    text: 'theme-text-main',
    textMain: 'theme-text-main',
    textMuted: 'theme-text-muted',
    header: theme === 'dark' ? 'bg-[#1e1e1e]' : 'bg-slate-50',
    rowHover: 'hover:bg-[var(--row-hover)]',
    selectedRow: theme === 'dark' ? 'bg-sky-400/20' : 'bg-indigo-50',
    topBar: 'theme-bg-panel border-[var(--border-main)]'
  }), [theme]);

  const SortIndicator = ({ column }: { column: SortKey }) => {
    if (sortConfig.key !== column || sortConfig.order === 'none') return null;
    return sortConfig.order === 'desc' ? <ChevronDown size={10} className="inline ml-0.5 text-indigo-500" /> : <ChevronUp size={10} className="inline ml-0.5 text-indigo-500" />;
  };

  if (!isAuthenticated) return <Login onLoginSuccess={handleLoginSuccess} theme={theme} onToggleTheme={toggleTheme} />;

  if (loading && transactions.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-screen theme-bg-main theme-text-main`}>
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-lg font-medium animate-pulse uppercase tracking-widest text-xs tracking-[0.2em]">Cargando Sesión...</p>
      </div>
    );
  }

  const asset = symbolMetrics.find(m => m.ticket === selectedSymbol);
  const currentAssetPriceColor = asset ? getPriceColorClass(asset.ticket) : 'text-[#089981]';
  const binanceKeyForChart = selectedSymbol.replace(/[-/]/g, '').replace('USD', 'USDT');
  const livePriceForChart = currentPrices[selectedSymbol]?.price || currentPrices[binanceKeyForChart]?.price || 0;

  const tradingStatsCards = [
    { 
      label: `En Cartera ${asset?.ticket.split(/[-/]/)[0] || ''}`, 
      value: `$ ${(asset?.currentVal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 
      isPrice: true,
      highlight: true, 
      pos: (asset?.totalProfit || 0) >= 0 
    },
    { label: (asset?.totalProfit || 0) >= 0 ? 'GANANCIA TOTAL' : 'PÉRDIDA TOTAL', value: `${(asset?.totalProfit || 0) >= 0 ? '+' : ''}${(asset?.totalProfit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, subValue: `(${(asset?.roi || 0) >= 0 ? '+' : ''}${(asset?.roi || 0).toFixed(2)}%)`, highlight: true, pos: (asset?.totalProfit || 0) >= 0, isPrice: true },
    { 
      label: 'Tenencia', 
      value: (
        <div className="flex items-center">
           <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black relative flex-shrink-0 overflow-hidden mr-1.5 ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
              <span>{asset?.ticket.charAt(0)}</span>
              <img src={getIconUrl(asset?.ticket || '')} className="absolute inset-0 w-full h-full object-cover bg-inherit" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
           </div>
           <span>{parseFloat((asset?.currentQty || 0).toFixed(8)).toString()}</span>
        </div>
      )
    },
    { label: 'Coste Neto', value: `${((asset?.totalBuy || 0) - (asset?.totalSell || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, subValue: `${(asset?.totalBuy || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} (Inv) - ${(asset?.totalSell || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} (Ven)` },
    { label: 'Promedio', value: `${(asset?.averageBuyPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, hasInfo: true },
  ];

  const historyStatsCards = [
    { label: 'Inversión Total', value: `${totalInvested.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: <Wallet size={12} className="text-blue-500" /> },
    { label: 'Total Vendido', value: `${totalSold.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: <Target size={12} className="text-indigo-500" /> },
    { label: liveChange24h >= 0 ? 'Ganancia 24h' : 'Pérdida 24h', value: `${liveChange24h >= 0 ? '+' : ''}${liveChange24h.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subValue: `(${liveChange24hPerc >= 0 ? '+' : ''}${liveChange24hPerc.toFixed(1)}%)`, highlight: true, pos: liveChange24h >= 0, isPrice: true },
    { label: livePnl >= 0 ? 'GANANCIA TOTAL' : 'PÉRDIDA TOTAL', value: `${livePnl >= 0 ? '+' : ''}${livePnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subValue: `(${livePnlPerc >= 0 ? '+' : ''}${livePnlPerc.toFixed(1)}%)`, highlight: true, pos: livePnl >= 0, isPrice: true },
    { label: 'Mejor Rendimiento', value: topPerformer?.symbolName || '---', subValue: topPerformer ? `${(topPerformer.roi || 0) >= 0 ? '+' : ''}${(topPerformer.roi || 0).toFixed(2)}%` : '', icon: <Coins size={12} className="text-orange-500" /> }
  ];

  const isStatsReady = historyStats.value !== 0;

  return (
    <div className={`flex flex-col h-screen ${themeClasses.bg} ${themeClasses.text} overflow-hidden transition-colors duration-300`}>
      {!isAnyContentMaximized && (
        <div className={`flex items-center justify-between px-6 py-1.5 border-b text-[10px] font-black uppercase tracking-widest flex-shrink-0 ${themeClasses.topBar}`}>
          <div className="flex items-center h-full flex-1 min-w-0 mr-8">
            <div className="flex items-center mr-6 flex-shrink-0">
                <img src={`https://drive.google.com/thumbnail?id=${ICON_LOGO_ID}&sz=w200`} className="h-14 w-14 object-contain" alt="Logo" />
            </div>
            <div className="flex-1 flex items-center h-10 min-w-0 border-l border-r border-white/5 px-4">
              <CryptoTicker theme={theme} />
            </div>
          </div>
          <div className="flex items-center h-full relative space-x-4 flex-shrink-0">
            <FearGreedWidget theme={theme} />
            
            {/* DATA DROPDOWN */}
            <div className="relative">
                <button 
                  onClick={() => setIsDataMenuOpen(!isDataMenuOpen)} 
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${isDataMenuOpen ? 'text-indigo-500' : 'text-slate-500 hover:text-indigo-400'}`}
                >
                   <Database size={16} className={isDataMenuOpen ? "text-indigo-500" : "text-slate-400"} /> 
                   <span>DATOS</span>
                   <ChevronDown size={12} className={`transition-transform duration-200 ${isDataMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isDataMenuOpen && (
                    <div className={`absolute left-0 top-full mt-2 w-48 rounded-xl border shadow-xl overflow-hidden py-1 z-50 animate-in fade-in zoom-in-95 duration-200 ${theme === 'dark' ? 'bg-[#1a1c22] border-[#2d3139]' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between px-4 py-3 hover:bg-white/5 group">
                            <button onClick={() => { switchToCloud(); setIsDataMenuOpen(false); }} className={`flex-1 flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest transition-colors ${dataSource === 'cloud' ? 'text-indigo-500' : (theme === 'dark' ? 'text-slate-400 group-hover:text-indigo-400' : 'text-slate-500 group-hover:text-indigo-600')}`}>
                                <Database size={14} />
                                <span>Base de Datos</span>
                            </button>
                            {currentUser?.role === 'SUPERADMIN' && (
                                <button onClick={(e) => { e.stopPropagation(); setIsDbHelpModalOpen(true); }} className="p-1 rounded-full text-slate-500 hover:text-indigo-500 transition-colors" title="Info"><HelpCircle size={12} /></button>
                            )}
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 hover:bg-white/5 group border-t border-white/5">
                            <button onClick={() => { switchToCsv(); setIsDataMenuOpen(false); }} className={`flex-1 flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest transition-colors ${dataSource === 'csv' ? 'text-indigo-500' : (theme === 'dark' ? 'text-slate-400 group-hover:text-indigo-400' : 'text-slate-500 group-hover:text-indigo-600')}`}>
                                <FileText size={14} />
                                <span>Subir CSV</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setIsCsvHelpModalOpen(true); }} className="p-1 rounded-full text-slate-500 hover:text-indigo-500 transition-colors" title="Info"><HelpCircle size={12} /></button>
                        </div>
                    </div>
                )}
            </div>

            {/* SAVE CSV BUTTON (Standalone) */}
            {dataSource === 'csv' && csvData.length > 0 && (
                <button 
                    onClick={() => setIsCsvUploadModalOpen(true)}
                    className="flex items-center space-x-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20 transition-all animate-in fade-in zoom-in-95"
                >
                    <Save size={14} />
                    <span>REGISTRAR CSV</span>
                </button>
            )}

            {/* MARKET (ASSET TYPE) DROPDOWN */}
            <div className="relative">
                <button 
                  onClick={() => setIsAssetMenuOpen(!isAssetMenuOpen)} 
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${isAssetMenuOpen ? 'text-indigo-500' : 'text-slate-500 hover:text-indigo-400'}`}
                >
                   {assetType === 'crypto' ? <Coins size={16} className={isAssetMenuOpen ? "text-indigo-500" : "text-slate-400"} /> : <Briefcase size={16} className={isAssetMenuOpen ? "text-indigo-500" : "text-slate-400"} />}
                   <span>{assetType === 'crypto' ? 'CRIPTO' : 'ACCIONES'}</span>
                   <ChevronDown size={12} className={`transition-transform duration-200 ${isAssetMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isAssetMenuOpen && (
                    <div className={`absolute left-0 top-full mt-2 w-40 rounded-xl border shadow-xl overflow-hidden py-1 z-50 animate-in fade-in zoom-in-95 duration-200 ${theme === 'dark' ? 'bg-[#1a1c22] border-[#2d3139]' : 'bg-white border-slate-200'}`}>
                        <button 
                          onClick={() => { switchToAssetType('crypto'); setIsAssetMenuOpen(false); }} 
                          className={`w-full flex items-center justify-between px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-colors ${assetType === 'crypto' ? (theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600') : (theme === 'dark' ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-50 text-slate-500')}`}
                        >
                           <div className="flex items-center space-x-2"><Coins size={14} /><span>Cripto</span></div>
                           {assetType === 'crypto' && <Check size={12} />}
                        </button>
                        <button 
                          onClick={() => { switchToAssetType('stocks'); setIsAssetMenuOpen(false); }} 
                          className={`w-full flex items-center justify-between px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-t ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} ${assetType === 'stocks' ? (theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600') : (theme === 'dark' ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-50 text-slate-500')}`}
                        >
                           <div className="flex items-center space-x-2"><Briefcase size={14} /><span>Acciones</span></div>
                           {assetType === 'stocks' && <Check size={12} />}
                        </button>
                    </div>
                )}
            </div>

            {(syncing || refreshing) && <div className="flex items-center space-x-2 text-indigo-500 animate-pulse"><Loader2 size={14} className="animate-spin" /></div>}
            
            {/* PORTFOLIO SELECTOR */}
            <div className="relative">
               <button onClick={() => setIsPortfolioMenuOpen(!isPortfolioMenuOpen)} className="flex items-center space-x-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all bg-indigo-500/10 text-indigo-500 border border-indigo-500/20 hover:bg-indigo-500/20">
                  <FolderOpen size={14} />
                  <span>{activePortfolio}</span>
                  <ChevronDown size={12} />
               </button>
               {isPortfolioMenuOpen && (
                   <div className={`absolute right-0 top-full mt-2 w-48 rounded-xl border shadow-xl overflow-hidden py-1 z-50 animate-in fade-in zoom-in-95 duration-200 ${theme === 'dark' ? 'bg-[#1a1c22] border-[#2d3139]' : 'bg-white border-slate-200'}`}>
                       {portfolioList.map((p, idx) => (
                           <button 
                             key={idx}
                             onClick={() => { setActivePortfolio(p); setIsPortfolioMenuOpen(false); }} 
                             className={`w-full flex items-center justify-between px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-colors ${p === activePortfolio ? (theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600') : (theme === 'dark' ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-50 text-slate-500')}`}
                           >
                              <span>{p}</span>
                              {p === activePortfolio && <Check size={14} />}
                           </button>
                       ))}
                       {portfolioList.length < (currentUser?.maxPortfolios || 1) && (
                           <button 
                             onClick={handleAddPortfolio}
                             className={`w-full flex items-center space-x-2 px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-t ${theme === 'dark' ? 'border-white/5 hover:bg-white/5 text-emerald-500' : 'border-slate-100 hover:bg-slate-50 text-emerald-600'}`}
                           >
                             <Plus size={14} />
                             <span>Agregar Portafolio</span>
                           </button>
                       )}
                   </div>
               )}
            </div>

             <div className="relative">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center justify-center focus:outline-none">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-black border transition-all ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-100'}`}>
                      <span>{currentUser?.username?.charAt(0).toUpperCase()}</span>
                  </div>
                </button>
                {isUserMenuOpen && (
                    <div className={`absolute right-0 top-full mt-2 w-48 rounded-xl border shadow-xl overflow-hidden py-1 z-50 animate-in fade-in zoom-in-95 duration-200 ${theme === 'dark' ? 'bg-[#1a1c22] border-[#2d3139]' : 'bg-white border-slate-200'}`}>
                        {(currentUser?.role === 'SUPERADMIN' || currentUser?.role === 'ADMIN') && (
                            <button onClick={() => { setIsAdminPanelOpen(true); setIsUserMenuOpen(false); }} className={`w-full flex items-center space-x-2 px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b ${theme === 'dark' ? 'hover:bg-white/5 text-indigo-400 border-white/5' : 'hover:bg-slate-50 text-indigo-600 border-slate-100'}`}><Users size={14} /><span>Administración</span></button>
                        )}
                        <button onClick={handleLogout} className={`w-full flex items-center space-x-2 px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-colors ${theme === 'dark' ? 'hover:bg-white/5 text-rose-400' : 'hover:bg-slate-50 text-rose-500'}`}><LogOut size={14} /><span>Salir</span></button>
                    </div>
                )}
             </div>
          </div>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <main className={`flex-1 flex flex-col min-h-0 ${isAnyContentMaximized ? 'p-0 overflow-hidden' : 'p-6 overflow-hidden'}`}>
          {!isAnyContentMaximized && (
            <div className="space-y-6 flex-shrink-0 mb-6 px-6">
              <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6">
                <div className={`flex-shrink-0 p-5 rounded-2xl border ${themeClasses.border} ${themeClasses.panel} shadow-sm flex flex-col justify-center space-y-2 min-w-[260px]`}>
                  <div className="flex items-center space-x-3">
                    {activeTab === 'trading' ? (
                      asset && (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black relative flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                          <span className="uppercase">{asset.ticket.charAt(0)}</span>
                          {getIconUrl(asset.ticket) && <img src={getIconUrl(asset.ticket)} className="absolute inset-0 w-full h-full object-cover bg-inherit" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                        </div>
                      )
                    ) : (
                       <div className={`w-10 h-10 flex items-center justify-center rounded-full ${theme === 'dark' ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}><Wallet size={20} className="text-indigo-500" /></div>
                    )}
                    <div>
                        <div className="flex items-baseline space-x-2">
                            <span className={`text-lg font-black ${themeClasses.textMain}`}>{activeTab === 'trading' ? (asset?.symbolName || 'Seleccione') : activeTab === 'history' ? 'Resumen' : activeTab === 'calendar' ? 'Calendario' : 'Transacciones'}</span>
                            {activeTab === 'trading' && asset && <span className={`${themeClasses.textMuted} font-bold text-[10px] uppercase tracking-wider`}>{asset?.ticket.split(/[-/]/)[0]}</span>}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{activeTab === 'trading' ? 'Precio Actual' : `${activePortfolio.toUpperCase()} TOTAL`}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline space-x-3">
                      <span className={`text-3xl font-black tracking-tight transition-all duration-500 ${activeTab === 'trading' ? currentAssetPriceColor : 'text-[#089981]'} ${(refreshing && activeTab !== 'trading') ? 'opacity-20 animate-pulse' : 'opacity-100'}`}>
                         {activeTab === 'trading' ? (refreshing ? formatLivePrice(0) : formatLivePrice(asset?.currentPrice || 0)) : (refreshing ? '0.00' : liveTotalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}
                      </span>
                      {activeTab === 'trading' && asset?.change24h !== undefined && !refreshing && asset.currentPrice !== 0 && (
                        <div className={`px-2 py-0.5 rounded-lg flex items-center text-[10px] font-black ${asset.change24h >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                          {asset.change24h >= 0 ? <ChevronUp size={12} className="mr-0.5" /> : <ChevronDown size={12} className="mr-0.5" />}
                          {Math.abs(asset.change24h).toFixed(2)}%
                        </div>
                      )}
                    </div>
                    {activeTab !== 'trading' && !refreshing && (
                      <div className={`px-2.5 py-1 rounded-lg flex items-center text-[11px] font-black ${ (livePnlPerc || 0) >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>{(livePnlPerc || 0) >= 0 ? <ArrowUpRight size={12} className="mr-1" /> : <ArrowDownRight size={12} className="mr-1" />}{Math.abs(livePnlPerc || 0).toFixed(2)}%</div>
                    )}
                  </div>
                </div>
                <div className="flex-1 relative min-w-0 group/stats">
                    {canScrollLeft && <div className={`absolute left-0 top-0 bottom-0 z-20 flex items-center pr-4 bg-gradient-to-r ${theme === 'dark' ? 'from-[#0f0f0f] to-transparent' : 'from-slate-50 to-transparent'}`}><button onClick={() => scrollStats('left')} className={`p-2 rounded-full shadow-lg ${theme === 'dark' ? 'bg-[#262626] text-white border-[#333]' : 'bg-white text-slate-700 border-slate-200'} border hover:scale-105 transition-all`}><ChevronLeft size={16} /></button></div>}
                    <div ref={statsScrollRef} onScroll={checkScroll} className="flex items-center justify-start gap-3 overflow-x-auto pb-2 xl:pb-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden scroll-smooth">
                        {(activeTab === 'trading' ? tradingStatsCards : historyStatsCards).map((card, i) => (
                            <div key={i} className={`flex-shrink-0 min-w-[160px] p-4 rounded-xl border ${themeClasses.border} ${themeClasses.panel} relative group overflow-hidden transition-all shadow-sm`}>
                              <div className="flex items-center space-x-1 mb-2">{(card as any).icon && <span className="mr-1">{(card as any).icon}</span>}<span className={`${themeClasses.textMuted} text-[11px] font-black uppercase tracking-tight`}>{card.label}</span>{(card as any).hasInfo && <Info size={12} className="text-slate-400 cursor-help" />}</div>
                              <div className={`flex items-baseline space-x-2 transition-all duration-500 ${refreshing && (card as any).isPrice ? 'opacity-20 animate-pulse' : 'opacity-100'}`}>
                                <div className={`text-xl font-black truncate ${card.highlight ? (card.pos ? 'text-[#089981]' : 'text-[#f23645]') : themeClasses.textMain}`}>{card.value}</div>
                                {(card as any).subValue && <div className={`text-xs font-bold ${card.highlight ? (card.pos ? 'text-[#089981]/70' : 'text-[#f23645]/70') : themeClasses.textMuted}`}>{(card as any).subValue}</div>}
                              </div>
                            </div>
                        ))}
                    </div>
                    {canScrollRight && <div className={`absolute right-0 top-0 bottom-0 z-20 flex items-center pl-4 bg-gradient-to-l ${theme === 'dark' ? 'from-[#0f0f0f] to-transparent' : 'from-slate-50 to-transparent'}`}><button onClick={() => scrollStats('right')} className={`p-2 rounded-full shadow-lg ${theme === 'dark' ? 'bg-[#262626] text-white border-[#333]' : 'bg-white text-slate-700 border-slate-200'} border hover:scale-105 transition-all`}><ChevronRight size={16} /></button></div>}
                </div>
                <div className="flex items-center space-x-3 flex-shrink-0">
                  <button onClick={toggleTheme} className={`p-2.5 rounded-lg border ${themeClasses.border} ${themeClasses.panel} hover:bg-indigo-500 hover:text-white transition-all shadow-sm`}>{theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}</button>
                </div>
              </div>
            </div>
          )}
          <div className={`flex-1 flex flex-col min-h-0 ${isAnyContentMaximized ? 'p-0 overflow-hidden' : 'p-6 overflow-hidden'}`}>
             {!isAnyContentMaximized && (
               <div className="flex-shrink-0 flex items-center justify-between border-b border-white/5 pb-0.5 mb-4 overflow-x-auto custom-scrollbar-horizontal">
                  <div className="flex space-x-8 min-w-max">
                     <button onClick={() => setActiveTab('trading')} className={`pb-4 text-[11px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === 'trading' ? 'text-indigo-500' : 'text-slate-500 hover:text-slate-300'}`}><div className="flex items-center space-x-2"><Activity size={14} /><span>Terminal Trading</span></div>{activeTab === 'trading' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 animate-in slide-in-from-left-4 duration-300"></div>}</button>
                     <button onClick={() => setActiveTab('history')} className={`pb-4 text-[11px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === 'history' ? 'text-indigo-500' : 'text-slate-500 hover:text-slate-300'}`}><div className="flex items-center space-x-2"><TrendingUp size={14} /><span>Evolución Patrimonial</span></div>{activeTab === 'history' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 animate-in slide-in-from-left-4 duration-300"></div>}</button>
                     <button onClick={() => setActiveTab('calendar')} className={`pb-4 text-[11px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === 'calendar' ? 'text-indigo-500' : 'text-slate-500 hover:text-slate-300'}`}><div className="flex items-center space-x-2"><CalendarIcon size={14} /><span>Calendario</span></div>{activeTab === 'calendar' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 animate-in slide-in-from-left-4 duration-300"></div>}</button>
                     <button onClick={() => setActiveTab('transactions')} className={`pb-4 text-[11px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === 'transactions' ? 'text-indigo-500' : 'text-slate-500 hover:text-slate-300'}`}><div className="flex items-center space-x-2"><Table size={14} /><span>Transacciones</span></div>{activeTab === 'transactions' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 animate-in slide-in-from-left-4 duration-300"></div>}</button>
                  </div>
               </div>
             )}
             <div className={`flex-1 relative min-h-0 ${isAnyContentMaximized ? 'overflow-hidden' : ''}`}>
               <div className={`${activeTab === 'trading' ? 'flex flex-col h-full' : 'hidden'} animate-in fade-in duration-500`}>
                 <div className={`relative group ${isChartMaximized ? 'flex-1' : 'flex-1 min-h-0'}`}>
                   <TradingChart 
                     key="main-trading-chart"
                     data={candles} 
                     isLoading={chartFetching}
                     transactions={activeTransactions} symbol={selectedSymbol} interval={selectedInterval} chartType={chartType} theme={theme} selectedTransactionId={selectedTransactionId}
                     breakEvenPrice={showBreakEven ? asset?.breakEven : undefined} isMeasureMode={isMeasureMode} onMeasureModeChange={setIsMeasureMode} 
                     showLabels={showChartLabels} onToggleLabels={() => setShowChartLabels(!showChartLabels)} 
                     showBreakEven={showBreakEven} onToggleBreakEven={() => setShowBreakEven(!showBreakEven)}
                     showEMA={showEMA} onToggleEMA={() => setShowEMA(!showEMA)}
                     emaPeriod={emaPeriod} onEmaPeriodChange={setEmaPeriod}
                     useDynamicOpacity={useDynamicOpacity} onToggleDynamicOpacity={() => setUseDynamicOpacity(!useDynamicOpacity)}
                     useUniformBubbleSize={useUniformBubbleSize} onToggleUniformBubbleSize={() => setUseUniformBubbleSize(!useUniformBubbleSize)}
                     symbolList={symbolMetrics} onSymbolChange={(s) => { setSelectedSymbol(s); setActiveTab('trading'); }} onIntervalChange={(val) => { setSelectedInterval(val); }}
                     onChartTypeChange={(type) => setChartType(type)} currentPrice={livePriceForChart} isFullscreen={isChartMaximized} onToggleFullscreen={(val) => setIsChartMaximized(val)}
                     isExpandedDown={isChartExpandedDown} onToggleExpandedDown={() => setIsChartExpandedDown(!isChartExpandedDown)} onToggleTheme={toggleTheme} onOpenTransactions={() => setIsTransactionsModalOpen(true)}
                     onDeleteTransaction={dataSource === 'cloud' ? confirmDelete : undefined}
                   />
                 </div>
                 {!isChartMaximized && !isChartExpandedDown && (
                   <div className="flex-shrink-0 h-[35%] min-h-[150px] pt-4 animate-in slide-in-from-bottom-5 duration-300">
                    <div className={`${themeClasses.panel} border ${themeClasses.border} rounded-xl overflow-hidden shadow-xl h-full flex flex-col`}>
                      <div className="flex-1 overflow-y-auto">
                        <table className="w-full text-left text-base">
                          <thead className={`${themeClasses.header} ${themeClasses.textMuted} uppercase font-black sticky top-0 z-10 text-xs`}>
                            <tr><th className="px-5 py-3 w-10"></th><th className="px-5 py-3">Tipo</th><th className="px-5 py-3">Precio</th><th className="px-5 py-3">Cantidad</th><th className="px-5 py-3">P&G</th><th className="px-5 py-3">Fecha</th><th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3 text-center">Acciones</th></tr>
                          </thead>
                          <tbody className={`divide-y ${theme === 'dark' ? 'divide-[#262626]' : 'divide-slate-200'}`}>
                            {[...activeTransactions].filter(t => t.ticket.split(/[-/]/)[0].toUpperCase() === selectedSymbol.split(/[-/]/)[0].toUpperCase()).sort((a, b) => b.timestamp - a.timestamp).map((t) => {
                              const isBuy = t.status?.toLowerCase().includes('compra') || t.status?.toLowerCase().includes('buy');
                              const isSelected = selectedTransactionId === t.id;
                              const currentSymPrice = currentPrices[t.ticket]?.price || 0;
                              const pnl = isBuy && currentSymPrice > 0 ? (currentSymPrice - t.price) * t.quantity : 0;
                              const pnlPerc = isBuy && t.price > 0 ? ((currentSymPrice - t.price) / t.price) * 100 : 0;
                              return (
                                <tr key={t.id} onClick={() => setSelectedTransactionId(t.id)} className={`cursor-pointer transition-colors ${isSelected ? themeClasses.selectedRow : themeClasses.rowHover}`}>
                                  <td className="px-5 py-3 align-middle"><div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black relative flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}><span>{t.ticket.charAt(0)}</span>{getIconUrl(t.ticket) && <img src={getIconUrl(t.ticket)} className="absolute inset-0 w-full h-full object-cover bg-inherit" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}</div></td>
                                  <td className="px-5 py-3"><span className={`font-black uppercase text-sm ${isBuy ? 'text-[#089981]' : 'text-[#f23645]'}`}>{isBuy ? 'Comprar' : 'Venta'}</span></td>
                                  <td className="px-5 py-3 font-mono">{(t.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                  <td className="px-5 py-3 font-mono">{isBuy ? '' : '-'}{(t.quantity || 0).toLocaleString('en-US', { maximumFractionDigits: 8 })}</td>
                                  <td className="px-5 py-3 font-mono">{isBuy && currentSymPrice > 0 ? (<div className={`flex flex-col ${pnl >= 0 ? 'text-[#089981]' : 'text-[#f23645]'}`}><div className="flex items-center font-black">{pnl >= 0 ? <ArrowUpRight size={12} className="mr-1" /> : <ArrowDownRight size={12} className="mr-1" />} {Math.abs(pnl).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div><div className="text-sm opacity-70 font-bold">{pnl >= 0 ? '+' : '-'}{Math.abs(pnlPerc).toFixed(2)}%</div></div>) : <span className="text-slate-500 text-sm">-</span>}</td>
                                  <td className="px-5 py-3"><div className="flex flex-col"><span className={`font-black ${themeClasses.textMain}`}>{t.date}</span><span className="text-sm text-slate-500 font-mono">{t.time}</span></div></td>
                                  <td className="px-5 py-3 text-right font-black font-mono">{(t.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                  <td className="px-5 py-3 text-center"><div className="flex items-center justify-center space-x-2"><button onClick={(e) => { e.stopPropagation(); setSelectedTransactionId(isSelected ? null : t.id); }} className={`p-1.5 rounded-full transition-all ${isSelected ? 'bg-sky-500 text-white' : 'text-slate-500 hover:bg-black/10'}`}><Eye size={14} /></button>{dataSource === 'cloud' && <button onClick={(e) => { e.stopPropagation(); confirmDelete(t.id); }} className="p-1.5 rounded-full text-slate-500 hover:bg-rose-500/10 hover:text-rose-500 transition-all"><Trash2 size={14} /></button>}</div></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                 )}
               </div>
               <div className={`${activeTab === 'history' ? 'block h-full' : 'hidden'} ${isHistoryMaximized ? 'h-full' : 'animate-in zoom-in-95 duration-500'}`}><PortfolioHistoryChart transactions={activeTransactions} theme={theme} symbolMetrics={symbolMetrics} activeTab={activeTab} onStatsUpdate={setHistoryStats} isMaximized={isHistoryMaximized} onToggleMaximize={setIsHistoryMaximized} /></div>
               <div className={`${activeTab === 'calendar' ? 'block h-full' : 'hidden'} ${isCalendarMaximized ? 'h-full overflow-hidden' : 'animate-in slide-in-from-bottom-10 duration-500'}`}><PortfolioCalendar transactions={activeTransactions} theme={theme} isMaximized={isCalendarMaximized} onToggleMaximize={setIsCalendarMaximized} icons={coinIcons} currentPrices={currentPrices} /></div>
               <div className={`${activeTab === 'transactions' ? 'flex flex-col h-full' : 'hidden'} animate-in fade-in duration-500`}>
                 <div className={`${themeClasses.panel} border ${themeClasses.border} rounded-2xl overflow-hidden shadow-2xl flex-1 flex flex-col`}>
                   <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
                     <div className="flex items-center space-x-2"><Table size={18} className="text-indigo-500" /><span className="text-[11px] font-black uppercase tracking-widest">Listado Movimientos</span></div>
                     
                     <div className={`flex p-1 rounded-xl border ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                        <button onClick={() => setTransactionsTypeFilter('all')} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${transactionsTypeFilter === 'all' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}>Todo</button>
                        <button onClick={() => setTransactionsTypeFilter('buy')} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${transactionsTypeFilter === 'buy' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}>Compras</button>
                        <button onClick={() => setTransactionsTypeFilter('sell')} className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${transactionsTypeFilter === 'sell' ? 'bg-rose-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}>Ventas</button>
                     </div>

                     <div className="flex flex-1 max-md items-center relative group"><Search size={14} className="absolute left-3 text-slate-500 group-focus-within:text-indigo-500" /><input type="text" placeholder="Filtrar..." value={transactionsSearchTerm} onChange={(e) => setTransactionsSearchTerm(e.target.value)} className={`w-full bg-black/5 border ${themeClasses.border} rounded-xl pl-9 pr-4 py-2 text-[11px] font-bold focus:outline-none transition-all`} /></div>
                     <div className="flex items-center space-x-4"><span className="text-[10px] font-bold text-slate-500 uppercase">Registros: {filteredFullTransactions.length}</span><button onClick={handleDownloadExcel} className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg shadow-emerald-600/20"><FileSpreadsheet size={14} /><span>DESCARGAR</span></button>{dataSource === 'cloud' && <button onClick={() => { setTransactionToEdit(null); setIsAddTransactionModalOpen(true); }} className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg shadow-indigo-600/20"><Plus size={14} /><span>AÑADIR TRANSACCIÓN</span></button>}</div>
                   </div>
                   <div className="flex-1 overflow-y-auto min-h-0">
                    <table className="w-full text-left">
                      <thead className={`${themeClasses.header} ${themeClasses.textMuted} uppercase font-black text-sm sticky top-0 z-10`}>
                        <tr><th className="px-6 py-4">Tipo</th><th className="px-6 py-4">Cantidad</th><th className="px-6 py-4">Símbolo</th><th className="px-6 py-4">Precio</th><th className="px-6 py-4">Total</th><th className="px-6 py-4 text-center">Acciones</th></tr>
                      </thead>
                      <tbody className={`divide-y ${theme === 'dark' ? 'divide-[#262626]' : 'divide-slate-200'}`}>
                        {filteredFullTransactions.sort((a,b) => b.timestamp - a.timestamp).map((t, idx) => {
                          const isBuy = t.status?.toLowerCase().includes('compra') || t.status?.toLowerCase().includes('buy');
                          return (
                            <tr key={t.id || idx} className={`transition-colors ${themeClasses.rowHover}`}>
                              <td className="px-6 py-4"><span className={`text-sm font-black px-2 py-1 rounded uppercase tracking-widest ${isBuy ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>{isBuy ? 'Compra' : 'Venta'}</span></td>
                              <td className="px-6 py-4 font-mono text-base">{t.quantity.toLocaleString('en-US', { maximumFractionDigits: 8 })}</td>
                              <td className="px-6 py-4"><div className="flex items-center space-x-2"><div className={`w-5 h-5 flex items-center justify-center text-xs font-black relative flex-shrink-0 rounded-full overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}><span>{t.ticket.charAt(0)}</span>{getIconUrl(t.ticket) && <img src={getIconUrl(t.ticket)} className="absolute inset-0 w-full h-full object-contain bg-inherit" alt="" onError={(e) => { e.currentTarget.style.display = 'none'} } />}</div><span className="font-black text-base">{t.symbolName}</span><span className="text-[13px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-black/5">{t.ticket.split(/[-/]/)[0]}</span></div></td>
                              <td className="px-6 py-4 font-mono text-base">{t.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td className="px-6 py-4 font-mono text-base font-black">{t.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td className="px-6 py-4 text-base"><div className="flex flex-col"><span className="font-bold">{t.date}</span><span className="text-[13px] opacity-60">{t.time}</span></div></td>
                              <td className="px-6 py-4 text-center">
                                <div className="flex items-center justify-center space-x-2">
                                  <button onClick={() => { setSelectedSymbol(t.ticket); setSelectedTransactionId(t.id); setActiveTab('trading'); }} className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500 hover:text-white transition-all"><Activity size={14} /></button>
                                  {dataSource === 'cloud' && (
                                    <>
                                      <button onClick={() => { setTransactionToEdit(t); setIsAddTransactionModalOpen(true); }} className="p-1.5 rounded-lg bg-sky-500/10 text-sky-500 hover:bg-sky-500 hover:text-white transition-all" title="Editar"><Edit2 size={14} /></button>
                                      <button onClick={(e) => { e.stopPropagation(); confirmDelete(t.id); }} className="p-1.5 rounded-lg bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all"><Trash2 size={14} /></button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                   </div>
                 </div>
               </div>
             </div>
          </div>
        </main>
        {(!isAnyContentMaximized || (isChartMaximized && activeTab === 'trading')) && (
          <aside className={`w-[440px] ${themeClasses.panel} border-l ${themeClasses.border} flex flex-col transition-colors duration-300 shadow-2xl z-20`}>
            {/* TÍTULO CENTRADO Y VALOR TOTAL */}
            <div className={`pt-4 pb-2 text-center bg-black/5 border-b border-transparent`}>
                <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-500/80">Rendimiento Cartera</h4>
                <div className={`text-[15px] font-normal font-mono mt-1 transition-all duration-500 ${theme === 'dark' ? 'text-white' : 'text-slate-900'} ${refreshing ? 'opacity-20 animate-pulse' : 'opacity-100'}`}>
                  {liveTotalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
            </div>
            {/* FILA DE RENDIMIENTO TEMPORAL (5 COLUMNAS) */}
            <div className={`px-2 py-3.5 border-b ${themeClasses.border} bg-black/5 grid grid-cols-5 gap-1`}>
              <div className="flex flex-col items-center justify-center border-r border-white/5 px-0.5">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">24h</span>
                <div className={`text-[13px] font-mono font-black ${!isStatsReady ? 'opacity-20' : ((historyStats.change24h || 0) >= 0 ? 'text-[#089981]' : 'text-[#f23645]')}`}>
                  {!isStatsReady ? '---' : `${(historyStats.change24h || 0) >= 0 ? '+' : '-'}${Math.abs(historyStats.change24h || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                </div>
                <div className={`flex items-center text-[12px] font-bold ${!isStatsReady ? 'opacity-20' : ((historyStats.change24hPerc || 0) >= 0 ? 'text-[#089981]/70' : 'text-[#f23645]/70')}`}>
                  {!isStatsReady ? '' : (historyStats.change24hPerc || 0) >= 0 ? <ChevronUp size={8} className="mr-0.5" /> : <ChevronDown size={8} className="mr-0.5" />}
                  {!isStatsReady ? '' : `${Math.abs(historyStats.change24hPerc || 0).toFixed(1)}%`}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center border-r border-white/5 px-0.5">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Sem.</span>
                <div className={`text-[13px] font-mono font-black ${!isStatsReady ? 'opacity-20' : ((historyStats.changeWeek || 0) >= 0 ? 'text-[#089981]' : 'text-[#f23645]')}`}>
                  {!isStatsReady ? '---' : `${(historyStats.changeWeek || 0) >= 0 ? '+' : '-'}${Math.abs(historyStats.changeWeek || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                </div>
                <div className={`flex items-center text-[12px] font-bold ${!isStatsReady ? 'opacity-20' : ((historyStats.changeWeekPerc || 0) >= 0 ? 'text-[#089981]/70' : 'text-[#f23645]/70')}`}>
                  {!isStatsReady ? '' : (historyStats.changeWeekPerc || 0) >= 0 ? <ChevronUp size={8} className="mr-0.5" /> : <ChevronDown size={8} className="mr-0.5" />}
                  {!isStatsReady ? '' : `${Math.abs(historyStats.changeWeekPerc || 0).toFixed(1)}%`}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center border-r border-white/5 px-0.5">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Mes</span>
                <div className={`text-[13px] font-mono font-black ${!isStatsReady ? 'opacity-20' : ((historyStats.changeMonth || 0) >= 0 ? 'text-[#089981]' : 'text-[#f23645]')}`}>
                  {!isStatsReady ? '---' : `${(historyStats.changeMonth || 0) >= 0 ? '+' : '-'}${Math.abs(historyStats.changeMonth || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                </div>
                <div className={`flex items-center text-[12px] font-bold ${!isStatsReady ? 'opacity-20' : ((historyStats.changeMonthPerc || 0) >= 0 ? 'text-[#089981]/70' : 'text-[#f23645]/70')}`}>
                  {!isStatsReady ? '' : (historyStats.changeMonthPerc || 0) >= 0 ? <ChevronUp size={8} className="mr-0.5" /> : <ChevronDown size={8} className="mr-0.5" />}
                  {!isStatsReady ? '' : `${Math.abs(historyStats.changeMonthPerc || 0).toFixed(1)}%`}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center border-r border-white/5 px-0.5">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Año</span>
                <div className={`text-[13px] font-mono font-black ${!isStatsReady ? 'opacity-20' : ((historyStats.changeYear || 0) >= 0 ? 'text-[#089981]' : 'text-[#f23645]')}`}>
                  {!isStatsReady ? '---' : `${(historyStats.changeYear || 0) >= 0 ? '+' : '-'}${Math.abs(historyStats.changeYear || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                </div>
                <div className={`flex items-center text-[12px] font-bold ${!isStatsReady ? 'opacity-20' : ((historyStats.changeYearPerc || 0) >= 0 ? 'text-[#089981]/70' : 'text-[#f23645]/70')}`}>
                  {!isStatsReady ? '' : (historyStats.changeYearPerc || 0) >= 0 ? <ChevronUp size={8} className="mr-0.5" /> : <ChevronDown size={8} className="mr-0.5" />}
                  {!isStatsReady ? '' : `${Math.abs(historyStats.changeYearPerc || 0).toFixed(1)}%`}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center px-0.5">
                <span className="text-[10px] font-black text-indigo-500/70 uppercase tracking-widest mb-1">{livePnl >= 0 ? 'G. Total' : 'P. Total'}</span>
                <div className={`text-[13px] font-mono font-black ${livePnl >= 0 ? 'text-[#089981]' : 'text-[#f23645]'}`}>
                  {livePnl >= 0 ? '+' : '-'}{Math.abs(livePnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
                <div className={`flex items-center text-[12px] font-bold ${livePnl >= 0 ? 'text-[#089981]/70' : 'text-[#f23645]/70'}`}>
                  {livePnl >= 0 ? <ChevronUp size={8} className="mr-0.5" /> : <ChevronDown size={8} className="mr-0.5" />}
                  {Math.abs(livePnlPerc).toFixed(1)}%
                </div>
              </div>
            </div>

            <div className={`p-4 border-b ${themeClasses.border} flex items-center justify-between gap-4`}><div className="flex-1 relative group"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 transition-colors group-focus-within:text-indigo-500" /><input type="text" placeholder="Buscar activo..." value={assetsSearchTerm} onChange={(e) => setAssetsSearchTerm(e.target.value)} className={`w-full bg-black/5 border ${themeClasses.border} rounded-xl pl-9 pr-4 py-2 text-[10px] font-black uppercase focus:outline-none focus:border-indigo-500/50 transition-all`} /></div><div className="flex items-center space-x-2"><button onClick={() => setHideSmallBalances(!hideSmallBalances)} className={`transition-colors ${hideSmallBalances ? 'text-indigo-500' : 'text-slate-500 hover:text-white'}`} title="Filtrar saldos bajos"><Filter size={14} /></button><button onClick={handleRefresh} className="text-slate-500 hover:text-indigo-500 transition-colors" title="Refrescar"><RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /></button></div></div>
            <div className={`px-4 py-2 flex items-center justify-between border-b ${themeClasses.border} bg-black/5`}><span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Activos en Cartera</span><span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-white/10 text-slate-400' : 'bg-slate-200 text-slate-600'}`}>{visibleAssets.length}</span></div>
            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-left table-fixed border-separate border-spacing-0">
                <thead className={`${theme === 'dark' ? 'bg-[#1e222d]' : 'bg-slate-100/80'} border-b ${themeClasses.border} sticky top-0 z-10`}>
                  <tr className="text-[10px] font-bold uppercase tracking-tighter select-none">
                    <th className="pl-2 py-1.5 w-[30px]"></th>
                    <th onClick={() => handleSort('rank')} className="py-1.5 w-[40px] text-slate-500 cursor-pointer hover:text-indigo-500 text-[9px]">Sym <SortIndicator column="rank" /></th>
                    <th onClick={() => handleSort('currentPrice')} className="py-1.5 text-right w-[70px] text-slate-500 cursor-pointer hover:text-indigo-500 text-[9px]">Live <SortIndicator column="currentPrice" /></th>
                    <th onClick={() => handleSort('displayTotal')} className="py-1.5 text-right w-[60px] text-slate-500 cursor-pointer hover:text-indigo-500 text-[9px]">Inv. <SortIndicator column="displayTotal" /></th>
                    <th onClick={() => handleSort('totalProfit')} className="py-1.5 text-right w-[65px] text-slate-500 cursor-pointer hover:text-indigo-500 text-[9px]">ROI <SortIndicator column="totalProfit" /></th>
                    <th onClick={() => handleSort('roi')} className="py-1.5 text-right w-[50px] text-slate-500 cursor-pointer hover:text-indigo-500 text-[9px]">ROI (%) <SortIndicator column="roi" /></th>
                    <th onClick={() => handleSort('ventasPerc')} className="pr-2 py-1.5 text-center w-[50px] text-slate-500 cursor-pointer hover:text-indigo-500 text-[9px]">% Rec <SortIndicator column="ventasPerc" /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-transparent">
                  {visibleAssets.map((m) => {
                    const isSelected = selectedSymbol === m.ticket;
                    const liqPerc = Math.round(m.ventasPerc || 0);
                    const isPricePending = refreshing || m.currentPrice === 0;
                    const effectiveROI = isPricePending ? 0 : m.roi;
                    const effectiveProfit = isPricePending ? 0 : m.totalProfit;

                    return (<tr key={m.ticket} onClick={() => { setSelectedSymbol(m.ticket); setSelectedTransactionId(null); setActiveTab('trading'); }} className={`cursor-pointer transition-colors ${themeClasses.rowHover} ${isSelected ? (theme === 'dark' ? 'bg-[#1e1e1e]' : 'bg-slate-100/50') : ''}`}><td className="pl-2 py-2 align-middle"><div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black relative flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}><span>{m.ticket.charAt(0)}</span>{getIconUrl(m.ticket) && <img src={getIconUrl(m.ticket)} className="absolute inset-0 w-full h-full object-cover bg-inherit" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}</div></td><td className="py-2"><span className="text-[13px] font-bold uppercase">{m.ticket.split(/[-/]/)[0]}</span></td><td className={`py-2 text-right text-[13px] font-mono whitespace-nowrap transition-all duration-500 ${getPriceColorClass(m.ticket)}`}>
                      <span className={isPricePending ? 'opacity-20 animate-pulse' : 'opacity-100 transition-opacity'}>{formatLivePrice(m.currentPrice)}</span>
                    </td><td className="py-2 text-right text-[13px] font-mono whitespace-nowrap">{(m.displayTotal || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td><td className={`py-2 text-right text-[13px] font-mono whitespace-nowrap transition-all duration-500 ${isPricePending ? 'text-slate-500 opacity-20' : (effectiveProfit >= 0 ? 'text-[#089981]' : 'text-[#f23645]')}`}>
                      <span className={isPricePending ? 'animate-pulse' : ''}>{(effectiveProfit || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    </td><td className={`py-2 text-right text-[13px] whitespace-nowrap transition-all duration-500 ${isPricePending ? 'text-slate-500 opacity-20' : (effectiveROI >= 0 ? 'text-[#089981]' : 'text-[#f23645]')}`}>
                      <span className={isPricePending ? 'animate-pulse' : ''}>{(effectiveROI || 0).toFixed(0)}%</span>
                    </td><td className="pr-2 py-2"><div className="flex flex-col items-center justify-center space-y-0.5"><div className={`w-10 h-[3px] rounded-full overflow-hidden ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-200'}`}><div className="h-full bg-orange-500 transition-all duration-500" style={{ width: `${Math.min(liqPerc, 100)}%` }} /></div><span className={`text-[10px] ${themeClasses.textMain}`}>{liqPerc.toFixed(1)}%</span></div></td></tr>);
                  })}
                </tbody>
              </table>
            </div>
            <div className={`p-4 border-t ${themeClasses.border} space-y-3`}>
              <div className="flex items-center justify-between text-[10px] font-black uppercase text-slate-500">
                <span>Inversión Bruta</span>
                <span className="text-indigo-400 font-mono">{(totalInvested || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setIsHoldingsModalOpen(true)} className="flex items-center justify-center space-x-2 bg-orange-600 hover:bg-orange-500 py-3 rounded-xl font-bold text-[10px] text-white uppercase tracking-widest transition-all shadow-lg shadow-orange-600/20"><PieChart size={14} /><span>Distribución</span></button>
                <button onClick={() => setIsSummaryModalOpen(true)} className="flex items-center justify-center space-x-2 bg-slate-800 hover:bg-slate-700 py-3 rounded-xl font-bold text-[10px] text-white uppercase tracking-widest transition-all shadow-lg shadow-slate-800/20"><Table size={14} /><span>REPORTE OPERATIVO</span></button>
                <button onClick={() => setIsHistogramModalOpen(true)} className="flex items-center justify-center space-x-2 bg-indigo-600 hover:bg-indigo-500 py-3 rounded-xl font-bold text-[10px] text-white uppercase tracking-widest transition-all shadow-lg shadow-indigo-600/20"><BarChart3 size={14} /><span>Rendimiento</span></button>
                <button onClick={() => setIsLatestBuysModalOpen(true)} className="flex items-center justify-center space-x-2 bg-emerald-600 hover:bg-emerald-500 py-3 rounded-xl font-bold text-[10px] text-white uppercase tracking-widest transition-all shadow-lg shadow-emerald-600/20"><ShoppingBag size={14} /><span>Ult Compras</span></button>
              </div>
            </div>
          </aside>
        )}
      </div>
      <SummaryModal isOpen={isSummaryModalOpen} onClose={() => setIsSummaryModalOpen(false)} data={symbolMetrics} theme={theme} icons={coinIcons} />
      <HoldingsModal isOpen={isHoldingsModalOpen} onClose={() => setIsHoldingsModalOpen(false)} data={symbolMetrics} theme={theme} icons={coinIcons} />
      <HistogramModal isOpen={isHistogramModalOpen} onClose={() => setIsHistogramModalOpen(false)} data={visibleAssets} theme={theme} icons={coinIcons} assetType={assetType} />
      <LatestBuysModal isOpen={isLatestBuysModalOpen} onClose={() => setIsLatestBuysModalOpen(false)} transactions={activeTransactions} currentPrices={currentPrices} theme={theme} icons={coinIcons} visibleSymbols={displayedAssetTickets} />
      <TransactionsModal 
        isOpen={isTransactionsModalOpen} 
        onClose={() => setIsTransactionsModalOpen(false)} 
        transactions={activeTransactions} 
        symbol={selectedSymbol} 
        theme={theme} 
        selectedId={selectedTransactionId} 
        onSelectTransaction={(id) => setSelectedTransactionId(id)} 
        currentPrice={currentPrices[selectedSymbol]?.price || currentPrices[selectedSymbol.replace(/[-/]/g, '').replace('USD', 'USDT')]?.price || 0} 
        onDeleteTransaction={dataSource === 'cloud' ? confirmDelete : undefined} 
        onAddTransaction={dataSource === 'cloud' ? () => { setIsAddTransactionModalOpen(true); setTransactionToEdit(null); } : undefined}
        onEditTransaction={dataSource === 'cloud' ? (t) => { setTransactionToEdit(t); setIsAddTransactionModalOpen(true); } : undefined}
        fetchCoinMetadata={fetchCoinMetadata} 
      />
      <AddTransactionModal isOpen={isAddTransactionModalOpen} onClose={() => { setIsAddTransactionModalOpen(false); setTransactionToEdit(null); }} onAdd={handleAddTransaction} theme={theme} availableSymbols={symbolMetrics.map(m => m.ticket)} currentUser={currentUser?.username} transactionToEdit={transactionToEdit} assetType={assetType} defaultSymbol={isTransactionsModalOpen ? selectedSymbol : undefined} portfolioList={portfolioList} />
      
      <CsvHelpModal isOpen={isCsvHelpModalOpen} onClose={() => setIsCsvHelpModalOpen(false)} theme={theme} />
      <DbHelpModal isOpen={isDbHelpModalOpen} onClose={() => setIsDbHelpModalOpen(false)} theme={theme} />
      <CsvUploadOtpModal 
        isOpen={isCsvUploadModalOpen} 
        onClose={() => setIsCsvUploadModalOpen(false)} 
        onSuccess={handleCsvSyncSuccess} 
        email={currentUser?.username || ''} 
        transactionCount={csvData.length} 
        theme={theme} 
      />

      {currentUser && <AdminPanel isOpen={isAdminPanelOpen} onClose={() => setIsAdminPanelOpen(false)} theme={theme} currentUser={currentUser} onUserUpdate={handleUserUpdate} />}
      <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept=".csv" style={{ display: 'none' }} />
      <ConfirmModal isOpen={isConfirmDeleteOpen} onClose={() => setIsConfirmDeleteOpen(false)} onConfirm={handleDeleteTransaction} title="¿Eliminar Transacción?" message="Esta acción borrará permanentemente el registro seleccionado." theme={theme} />
    </div>
  );
};

export default App;